import { TbCircleCheck, TbX } from "solid-icons/tb";
import { createEffect, type JSX, on, onCleanup, onMount, type ParentComponent, Show } from "solid-js";
import { Portal } from "solid-js/web";
import type { DetectedOS } from "~/lib/useOS";
import {
  ChiefInApplications,
  ChiefInStartMenu,
  DmgInDownloads,
  DragToApplications,
  SetupInDownloads,
  SmartScreenRunAnyway,
} from "./internal/InstallSteps";

const DOWNLOAD_FRAME_ID = "chief-download-frame";

/**
 * Kick off the installer download without navigating the page. The endpoint
 * 302-redirects to a signed installer; pointing a hidden iframe at it lets the
 * browser handle the file transfer while this modal stays in view. A bare
 * anchor would instead navigate the top window to the endpoint.
 */
const startDownload = (href: string) => {
  if (typeof document === "undefined") return;
  let frame = document.getElementById(DOWNLOAD_FRAME_ID) as HTMLIFrameElement | null;
  if (!frame) {
    frame = document.createElement("iframe");
    frame.id = DOWNLOAD_FRAME_ID;
    frame.style.display = "none";
    document.body.appendChild(frame);
  }
  frame.src = href;
};

const Step: ParentComponent<{ n: number; art: JSX.Element }> = (props) => (
  <li class="flex flex-col">
    {props.art}
    <p class="mt-3 flex items-start gap-2 text-start text-xs leading-snug text-secondary-text">
      <span class="grid size-5 shrink-0 place-items-center rounded-full bg-brand text-[11px] font-bold text-on-brand">
        {props.n}
      </span>
      <span class="pt-0.5">{props.children}</span>
    </p>
  </li>
);

/**
 * Post-click confirmation for the installer download. Opening the modal also
 * starts the download (via a hidden iframe), so the trigger only has to flip
 * `open`. `href` powers both the download and the manual-retry link, and `os`
 * selects which install walkthrough the visitor is shown.
 */
export function DownloadStartedModal(props: { open: boolean; href?: string; os?: DetectedOS; onClose: () => void }) {
  const isWindows = () => props.os === "windows";

  onMount(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && props.open) props.onClose();
    };
    document.addEventListener("keydown", onKey);
    onCleanup(() => document.removeEventListener("keydown", onKey));
  });

  createEffect(
    on(
      () => props.open,
      (open) => {
        if (open && props.href) startDownload(props.href);
      }
    )
  );

  return (
    <Show when={props.open}>
      <Portal>
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="download-started-title"
          class="fixed inset-0 z-100 flex animate-[fadeIn_160ms_ease-out_both] items-center justify-center overflow-y-auto bg-black/60 px-4 py-6 backdrop-blur-sm"
          onClick={(e) => {
            if (e.target === e.currentTarget) props.onClose();
          }}
        >
          <div class="relative my-auto w-full max-w-4xl animate-[fadeUp_220ms_ease-out_both] rounded-2xl border border-primary-border bg-canvas p-6 text-center shadow-2xl shadow-black/40 sm:p-9">
            <button
              type="button"
              onClick={() => props.onClose()}
              class="absolute top-3 right-3 cursor-pointer rounded-md p-1.5 text-secondary-text transition-colors hover:bg-panel hover:text-primary-text"
              aria-label="Close"
            >
              <TbX class="size-4" />
            </button>

            <span class="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 px-3 py-1 text-xs font-semibold text-emerald-500">
              <TbCircleCheck class="size-4" aria-hidden="true" />
              Download started
            </span>

            <h2 id="download-started-title" class="mt-4 mb-2 text-2xl font-bold sm:text-3xl">
              Thanks for downloading Chief!
            </h2>
            <p class="mx-auto mb-9 max-w-xl text-sm leading-relaxed text-secondary-text">
              Your download will begin automatically.{" "}
              <Show when={props.href} fallback="Check your Downloads folder if you don't see it.">
                {(href) => (
                  <>
                    If it didn't start,{" "}
                    <a
                      href={href()}
                      onClick={(e) => {
                        e.preventDefault();
                        startDownload(href());
                      }}
                      class="font-semibold text-brand underline underline-offset-2 hover:opacity-90"
                    >
                      download Chief manually
                    </a>
                    .
                  </>
                )}
              </Show>
            </p>

            <ol class="grid gap-6 sm:grid-cols-3">
              <Show
                when={isWindows()}
                fallback={
                  <>
                    <Step n={1} art={<DmgInDownloads />}>
                      Open <span class="font-semibold text-primary-text">Chief.dmg</span> from your{" "}
                      <span class="font-semibold text-primary-text">Downloads</span> folder
                    </Step>
                    <Step n={2} art={<DragToApplications />}>
                      Drag the <span class="font-semibold text-primary-text">Chief</span> icon into your{" "}
                      <span class="font-semibold text-primary-text">Applications</span> folder
                    </Step>
                    <Step n={3} art={<ChiefInApplications />}>
                      Open the <span class="font-semibold text-primary-text">Chief</span> app from your{" "}
                      <span class="font-semibold text-primary-text">Applications</span> folder
                    </Step>
                  </>
                }
              >
                <Step n={1} art={<SetupInDownloads />}>
                  Run <span class="font-semibold text-primary-text">Chief-setup.exe</span> from your{" "}
                  <span class="font-semibold text-primary-text">Downloads</span> folder
                </Step>
                <Step n={2} art={<SmartScreenRunAnyway />}>
                  If SmartScreen appears, choose <span class="font-semibold text-primary-text">More info</span> then{" "}
                  <span class="font-semibold text-primary-text">Run anyway</span>
                </Step>
                <Step n={3} art={<ChiefInStartMenu />}>
                  Launch <span class="font-semibold text-primary-text">Chief</span> from the{" "}
                  <span class="font-semibold text-primary-text">Start</span> menu
                </Step>
              </Show>
            </ol>
          </div>
        </div>
      </Portal>
    </Show>
  );
}
